import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'

import { exportAssignmentsAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import LineBarGraph from '../../common/LineBarGraph'
import { Icons, } from '../../../utils/utilities'
import api from '../../../service/api'


const columns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', width: "70px", align: "center", padding: "10px 20px" },
    { id: 2, field: "faculty_name", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.assignment_created_by}?legacy=${Boolean( row.legacy_system_data )}`, renderCell: row => row.faculty_name?.toLowerCase(), name: "Faculty", cellStyles: { textTransform: "capitalize" }, width: "150px", padding: "10px 20px" },
    { id: 3, field: "assignment_created_by", filterable: true, name: 'Emp ID', linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.assignment_created_by}?legacy=${Boolean( row.legacy_system_data )}`, padding: "10px 20px" },
    { id: 6, field: "institute", filterable: true, name: "Institute", cellStyles: { textTransform: "capitalize" }, width: "100px", padding: "10px 20px" },
    { id: 6, field: "department", filterable: true, renderCell: row => `${row.department?.toLowerCase()}`, name: "Department", cellStyles: { textTransform: "capitalize" }, width: "120px", padding: "10px 20px" },
    { id: 4, field: "count", sortable: true, align: "center", style: { align: "center" }, name: "Assignments", dataType: "number", width: "150px", padding: "10px 20px" },
    { id: 5, field: "submissions_received", sortable: true, align: "center", style: { align: "center" }, name: "Submissions", dataType: "number", width: "150px", padding: "10px 20px" },
]

const AssignmentsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortBySubmissionsCount: "0", sortByAssignmentCount: "0" } )

    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [showzeroassignmentsfaculties, setShowzeroassignmentsfaculties] = useState( false )

    const { layout, palette } = useTheme()

    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            return ( item.legacy_system_data && +legacy_department_id === +item.department_id ) || ( !item.legacy_system_data && +department_id === +item.department_id )
        }
        return true
    }

    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getAssignmentGraph( { month, institute, department } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the assignment graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, zeroAssignmentFaculties = [], last12MonthsStats = {} } } } = await api.audit.getAssignmentStats( { month, showzeroassignmentsfaculties, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            let alldata = facultyStats
            if ( showzeroassignmentsfaculties ) {
                alldata = [...zeroAssignmentFaculties, ...facultyStats]
            }
            setFacultyStats( alldata )
            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the assignment audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute, showzeroassignmentsfaculties] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    const filtered = facultyStats.filter( filterFunc )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filters */}
            <AuditPageStatsFilterBar handleShowFacultyWithZeroResourceChange={e => setShowzeroassignmentsfaculties( e.target.checked )} showzeroassignmentsfaculties={showzeroassignmentsfaculties} resource="assignments" listResourceBtnLabel="View Assignments" title="Assignments statistics" />

            {/* Data */}
            {!statsError && !loading && view === 'tabular' && <CustomTable
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                globalSearch="Search by faculty name or employee code..."
                pageSize={50}
                columns={columns}
                data={filtered}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => exportAssignmentsAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-assignments-audit-report` )
                    }
                ]}
            />}

            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}

            {!loading && !loadingGraphData && view !== 'tabular' && filtered.length > 0 &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    {!graphError ? <LineBarGraph graphTitle="" barLegend='Submissions submitted' yAxisName="Assignments" xAxisName="Date" y2AxisName="Submissions" lineLegend='Assignments created' barValuesKey="submissions" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'assignments_created'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner padding="0" waitingMessage="Loading assignment audit report please wait..." />}
        </Box >
    )
}

export default AssignmentsStats
